import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Storefront, CookingPot, Bell, CheckCircle, ClipboardText } from "@phosphor-icons/react";
import { useRealtimeChannel } from "@/lib/realtime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Queue() {
  const [params] = useSearchParams();
  const venueId = params.get("venue");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    if (!venueId) return;
    try {
      const r = await axios.get(`${API}/public/queue/${venueId}`);
      setData(r.data);
    } catch (e) {
      console.error("queue", e);
      setErr(e?.response?.data?.detail || "Could not load queue");
    }
  }, [venueId]);

  useEffect(() => {
    if (!venueId) { setErr("Add ?venue=<venue-id> to the URL"); return; }
    load();
    const t = setInterval(load, 3000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [venueId, load]);

  // Real-time: queue.updated → refetch immediately. Polling stays as fallback.
  useRealtimeChannel({
    path: venueId ? `/api/ws/queue/${venueId}` : null,
    enabled: !!venueId,
    onEvent: (msg) => {
      if (msg?.type === "queue.updated") load();
    },
  });

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--ink-900)] text-[var(--sand-50)] p-8">
        <div className="text-center">
          <div className="label-eyebrow mb-2 text-[var(--sand-200)]">Queue display</div>
          <h1 className="font-heading text-3xl font-semibold">{err}</h1>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[var(--ink-900)] text-[var(--sand-50)]">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[var(--ink-900)] text-[var(--sand-50)] p-6 sm:p-10" data-testid="queue-display">
      <header className="flex items-center justify-between mb-8 sm:mb-12">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-[var(--terracotta)] flex items-center justify-center">
            <Storefront size={24} color="#fff" weight="fill" />
          </div>
          <div>
            <div className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{data.business?.name || "Order Queue"}</div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--sand-200)] opacity-70">{data.venue?.name}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-3xl sm:text-4xl tabular tracking-tight">{new Date(now).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sand-200)] opacity-70 mt-1">Live · refreshes every 3 s</div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
        <Column
          tone="received"
          icon={ClipboardText}
          label="Order Received"
          tickets={data.received || []}
          empty="No new orders yet."
          data-testid="queue-received"
        />
        <Column
          tone="preparing"
          icon={CookingPot}
          label="Preparing"
          tickets={data.preparing}
          empty="No orders in the kitchen right now."
          data-testid="queue-preparing"
        />
        <Column
          tone="ready"
          icon={Bell}
          label="Ready"
          tickets={data.ready}
          empty="Nothing ready yet."
          data-testid="queue-ready"
        />
      </div>

      {data.completed?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-white/10">
          <div className="flex items-center gap-2 text-[var(--sand-200)] opacity-80 mb-3">
            <CheckCircle size={14} /> <span className="text-xs uppercase tracking-[0.18em] font-medium">Recently collected</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="queue-completed">
            {data.completed.map((t) => (
              <span key={t.ticket_number} className="font-mono tabular text-sm px-3 py-1 rounded-full bg-white/5 text-[var(--sand-200)] opacity-60">#{t.ticket_number}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Column({ icon: Icon, label, tickets, empty, tone, "data-testid": dataTestId }) {
  // iter_171 — 3-tone palette: received (sand), preparing (terracotta), ready (moss).
  const accent = tone === "ready"
    ? "var(--moss)"
    : tone === "received"
      ? "var(--sand-200)"
      : "var(--terracotta)";
  return (
    <section className="relative" data-testid={dataTestId || (tone === "ready" ? "queue-ready" : "queue-preparing")}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: `${accent}22` }}>
            <Icon size={20} weight={tone === "ready" ? "fill" : "regular"} color={accent} />
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{label}</h2>
        </div>
        <span className="font-mono tabular text-base sm:text-lg text-[var(--sand-200)] opacity-70">{tickets.length}</span>
      </div>
      {tickets.length === 0 ? (
        <div className="rounded-lg border border-dashed border-white/10 p-10 text-center text-[var(--sand-200)] opacity-50 text-sm">{empty}</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {tickets.map((t) => (
            <div
              key={t.ticket_number}
              data-testid={`queue-ticket-${t.ticket_number}`}
              className="rounded-lg p-4 sm:p-5 fade-up"
              style={{
                background: tone === "ready" ? `linear-gradient(180deg, ${accent}22 0%, ${accent}0a 100%)` : "rgba(255,255,255,0.04)",
                border: `1.5px solid ${tone === "ready" ? accent + "55" : "rgba(255,255,255,0.08)"}`,
              }}
            >
              <div className="font-mono font-semibold text-3xl sm:text-5xl tabular tracking-tight" style={{ color: tone === "ready" ? accent : "var(--sand-50)" }}>#{t.ticket_number}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--sand-200)] opacity-60 mt-1 tabular">{t.item_count} {t.item_count === 1 ? "item" : "items"}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
